import { emptyFunction } from "@utils/utils";

import {
  useUpdateOrganizationTemplateLogoUrlMutation,
  useUpdateReportPageHeadingMutation,
} from "@api/graphql/types-and-hooks";
import { BespokeInputField } from "@components/FieldComponent/BespokeInputField";
import { FieldComponent } from "@components/FieldComponent/FieldComponent";
import UploadLogo from "@components/ReportHeaderSection/UploadLogo";
import {
  useGetS3BucketLogoUrlRetriever,
  useGetS3BucketUploadLogoCallback,
} from "@components/ReportHeaderSection/hooks";
import { SectionWidget } from "@components/SectionWidget/SectionWidget";
import { notifyError } from "@components/notifications/notifications";
import { ReportNode } from "@providers/ReportProvider";

interface ReportHeaderSectionProps {
  reportIsCompleted: boolean;
  report: ReportNode;
  refetchData: () => Promise<void> | unknown;
}

export const ReportHeaderSection: React.FC<ReportHeaderSectionProps> = ({ reportIsCompleted, report, refetchData }) => {
  const permissions = { canEdit: false, canDelete: false };

  const S3BucketUrlRetriever = useGetS3BucketLogoUrlRetriever({ reportId: report.id });
  const S3BucketUploadCallback = useGetS3BucketUploadLogoCallback({ reportId: report.id, refetch: refetchData });
  const updateContent = useUpdateReportPageHeadingMutation();
  const updateOrganizationTemplateLogoMutation = useUpdateOrganizationTemplateLogoUrlMutation();

  const { isPending } = updateContent;

  const handleOnSave = async (content: string) => {
    await updateContent
      .mutateAsync({
        input: { reportId: report?.id ?? "<missing-report-id>", pageHeadingText: content },
      })
      .then((data) => {
        if (data.updateReportPageHeading?.status) {
          refetchData();
        }
      })
      .catch((error) => {
        console.error("Error while updating report heading : ", error);
        notifyError("Error Updating Report Heading");
      });
  };

  const onSave = async () => {
    await updateOrganizationTemplateLogoMutation
      .mutateAsync({
        input: { reportId: report?.id ?? "<missing-report-id>" },
      })
      .then((data) => {
        if (data.updateOrganizationTemplateLogoUrl?.status) {
          refetchData();
        }
      })
      .catch((error) => {
        console.error("Error removing the header logo : ", error);
        notifyError("Error removing the header logo");
      });
  };

  return (
    <SectionWidget
      sectionType={"regular"}
      key="Report-Header"
      id="ReportHeader"
      onAddContent={emptyFunction}
      onEditSection={emptyFunction}
      permissions={permissions}
      sectionData={{ heading: "Report Header", icon: "document" }}
      supportedContentTypes={[]}
      placeholder=""
      allowInLineEdit={false}
      setIsInLineEditMode={() => {}}
      isInLineEditMode={false}
      actionButtonMenu={[]}
      isLoading={false}
      isError={false}
    >
      {report?.reportTemplate?.organizationLogoAllowed && (
        <>
          <div key={`logo-logo`}>
            <FieldComponent
              key={"logo-logo"}
              id={"logo-logo"}
              onEditTitle={emptyFunction}
              permissions={permissions}
              title={"Upload Logo"}
              placeholder=""
            >
              <div
                className={`mt-2 pr-6 ${report?.organization?.templateLogoSignedUrl || reportIsCompleted ? "-mb-2" : "-mb-8"} `}
              >
                <UploadLogo
                  S3BucketUrlRetriever={(params) =>
                    S3BucketUrlRetriever({
                      ...params,
                    })
                  }
                  S3BucketUploadCallback={(params) =>
                    S3BucketUploadCallback({
                      ...params,
                    })
                  }
                  reportFileUploadCompleteCallback={() => Promise.resolve()}
                  fileParseStatusRetriever={(fileId) =>
                    new Promise((resolve) => {
                      setTimeout(() => resolve({ fileId, status: "done" }), 1000);
                    })
                  }
                  removeFileCallback={() => Promise.resolve()}
                  logoSignedUrl={report?.organization?.templateLogoSignedUrl ?? ""}
                  onSave={onSave}
                  displayOnlyMode={reportIsCompleted}
                />
              </div>
            </FieldComponent>
          </div>
        </>
      )}

      <div key={`page-heading`} className="-mb-4">
        <FieldComponent
          key={"page-heading"}
          id={"page-heading"}
          onEditTitle={emptyFunction}
          permissions={permissions}
          title={"Page Heading"}
          placeholder="Page Heading"
        >
          <div className={reportIsCompleted ? "" : "-mt-6"}>
            <BespokeInputField
              isSaving={isPending}
              onSave={async ({ content }: { content: string }) => {
                await handleOnSave(content);
              }}
              existingContent={report?.headerPageHeading ?? ""}
              placeholder="Enter Page Heading"
              inputType={"textarea"}
              displayOnlyMode={reportIsCompleted}
              autoSave={true}
            />
          </div>
        </FieldComponent>
      </div>
    </SectionWidget>
  );
};
